import { useEffect, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Loader2, SearchX } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import Layout from "@/components/Layout";
import ArticleCard from "@/components/ArticleCard";
import { useCategories } from "@/context/CategoriesContext";
import { paletteFor } from "@/lib/categories";

const PAGE_SIZE = 12;

export default function CategoryPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const search = searchParams.get("search") || "";
  const { bySlug } = useCategories();

  const isAll = slug === "all";
  const cat = isAll ? null : bySlug(slug);
  const meta = cat ? paletteFor(cat.color) : null;

  const buildQuery = (skip) => {
    const p = new URLSearchParams();
    if (!isAll) p.set("category_slug", slug);
    if (search) p.set("search", search);
    p.set("limit", String(PAGE_SIZE));
    p.set("skip", String(skip));
    return p.toString();
  };

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["category", slug, search],
    queryFn: ({ pageParam = 0 }) =>
      api.get(`/articles?${buildQuery(pageParam)}`).then((r) => r.data),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((n, p) => n + p.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
  });

  const items = data?.pages.flatMap((p) => p.items) || [];
  const total = data?.pages?.[0]?.total || 0;

  const title = search
    ? `Резултати за „${search}“`
    : cat?.name || (isAll ? "Всички новини" : slug);

  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: "600px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const seoTitle = search
    ? `Търсене: ${search}`
    : cat?.name || (isAll ? "Всички новини" : title);
  const seoDesc = cat
    ? `Последни новини от рубрика „${cat.name}“ за район Подуяне, София.`
    : "Всички последни новини за район Подуяне, София.";

  return (
    <Layout>
      <Seo title={seoTitle} description={seoDesc} noindex={!!search} />
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8" data-testid="category-page">
        <div className="mb-8 border-b border-slate-200 pb-5">
          <div className="flex items-center gap-3">
            {meta && <span className={`h-8 w-1.5 rounded-full ${meta.solid}`} />}
            <h1 className="font-display text-3xl font-extrabold text-slate-900 sm:text-4xl">
              {title}
            </h1>
          </div>
          {!isLoading && (
            <p className="mt-2 text-sm text-slate-500">
              {total} {total === 1 ? "статия" : "статии"}
            </p>
          )}
        </div>

        {isLoading ? (
          <div className="flex min-h-[40vh] items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <SearchX className="h-12 w-12 text-slate-300" />
            <h2 className="mt-4 font-display text-xl font-bold text-slate-800">
              Няма намерени статии
            </h2>
            <p className="mt-2 text-slate-500">
              Опитайте с друга рубрика или ключова дума.
            </p>
            <Link
              to="/"
              className="mt-6 inline-flex rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#0041A8]"
            >
              Към началото
            </Link>
          </div>
        ) : (
          <>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="category-grid">
              {items.map((a) => (
                <ArticleCard key={a.id} article={a} variant="default" />
              ))}
            </div>

            <div ref={sentinelRef} className="h-px w-full" aria-hidden="true" />

            {isFetchingNextPage && (
              <div className="mt-8 flex justify-center" data-testid="category-loading-more">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            )}

            {hasNextPage && !isFetchingNextPage && (
              <div className="mt-8 flex justify-center">
                <button
                  onClick={() => fetchNextPage()}
                  data-testid="category-load-more-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-700 transition-colors hover:border-primary hover:text-primary"
                >
                  Зареди още
                </button>
              </div>
            )}

            {!hasNextPage && items.length > PAGE_SIZE && (
              <p className="mt-8 text-center text-sm text-slate-400">
                Това са всички статии.
              </p>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
